import { rupeesToCrore, rupeesToLakh } from '@/lib/broker-format'
import { abs, cmp, money, toChartNumber, type Money } from '@/lib/money'

/**
 * The layout and the arithmetic behind Sector flow's "Broker by sector" grid,
 * kept out of `BrokerBySectorCard.tsx` so the card and its skeleton
 * (`BrokerBySectorSkeleton.tsx`, which the route's pending page imports and
 * so must stay light) draw the same columns at the same widths.
 *
 * The grid draws EVERY sector mm-be sends (15 on dev), busiest first, and
 * scrolls sideways inside the card: the owner wants to reach all of them, so
 * an earlier cut to the busiest six is gone. The Broker column is sticky, so
 * a house's name stays beside its row however far the sectors scroll.
 */

/**
 * One sector column, the same at every width: 112px fits the longest short label
 * ("Manufacturing", 80px of text) beside the sort caret and the 8px right inset its figures share,
 * and a "−418.96" tile with room to spare. Fixed rather than shared out, so a column never narrows
 * into an ellipsis on a phone; the scroller takes the overflow instead.
 */
export const SECTOR_COLUMN_WIDTH = 'w-28'

/**
 * The sticky Broker column's width: 112px on a phone, which still shows two
 * whole sector columns beside it in a 390px screen's 326px card, and 176px
 * from `@md` (448px) of the card, where most short names print whole.
 */
export const BROKER_COLUMN_WIDTH = 'w-28 @md/bysector:w-44'

/**
 * A cell of the sticky Broker column. The card's own fill makes it opaque, so
 * tiles scrolled past slide under it; the 4px `ring` in the same fill covers
 * the border-spacing gaps round it too, or each 4px gap between two names
 * would show a sliver of the tile passing beneath. `left-1`, not `left-0`:
 * the column starts 4px in (the table's own border-spacing), so it sticks
 * where it already sits instead of jumping 4px left on the first scroll.
 * `z-1` lifts it over the (unpositioned) tiles and nothing else.
 */
export const STICKY_CELL = 'sticky left-1 z-1 bg-card p-0 ring-4 ring-card'

/**
 * The box the grid scrolls in, as `ScrollArea` draws it, for the skeleton to
 * copy without importing `ScrollArea` (a component module the pending page
 * must not pull in): its wrapper, with `-m-1` pulling the table's own 4px
 * outer border-spacing back to the card's inset, and its viewport, the same
 * `scrollbar-slim` scroller, so a platform that draws a scrollbar draws it
 * under both, at the same height.
 */
export const SCROLL_WRAPPER = '-m-1'
export const SKELETON_VIEWPORT = 'scrollbar-slim min-w-0 grow overflow-x-auto'

/**
 * The grid itself. `border-separate` + `border-spacing-1` puts a 4px gap
 * between tiles. `w-max` sizes it to its columns (so it overflows and
 * scrolls) and `min-w-full` stretches it across the card on a day with only a
 * few sectors. `relative` makes the table the containing block of every
 * `sr-only` span in it: those are `position: absolute`, and with no
 * positioned ancestor inside the scroller they would escape its clip and
 * stretch the page sideways (DataTable's scroller hit exactly that).
 */
export const GRID_TABLE = 'relative w-max min-w-full border-separate border-spacing-1'

/** Header row and body row heights: the tile is the row. */
export const HEAD_ROW_HEIGHT = 'h-8'
export const TILE_HEIGHT = 'h-8'

/** The unit every figure in the grid, and its legend, is printed in. */
export interface GridUnit {
  /** As the subtitle and a screen reader say it: "Rs lakh", "Rs 3.27 crore". */
  word: 'lakh' | 'crore'
  toUnit: (rupees: Money) => Money
}

const LAKH: GridUnit = { word: 'lakh', toUnit: rupeesToLakh }
const CRORE: GridUnit = { word: 'crore', toUnit: rupeesToCrore }

const ONE = money('1')
const HUNDRED = money('100')

/**
 * ONE unit for the whole grid, picked from the figures it will print: the one
 * that puts the most of them at one or two whole digits ("3.27", "12.40"),
 * the shape the frame draws. Per-cell units ("12.40 Cr" beside "3.27 L")
 * would make the tiles incomparable at a glance, which is the grid's point.
 *
 * On dev's June 2026 day the cells run from a few thousand rupees to 4.19
 * crore: in lakh 128 of the 171 traded cells land at 1–99, in crore only 13,
 * so lakh wins. A heavier day tips it to crore on its own. On a tie, the unit
 * with fewer three-digit figures wins (a narrower tile), then lakh (so a day
 * of small figures still prints them rather than "0.00").
 */
export const pickGridUnit = (values: readonly Money[]): GridUnit => {
  const score = (unit: GridUnit) => {
    let readable = 0
    let wide = 0
    for (const value of values) {
      const scaled = abs(unit.toUnit(value))
      if (cmp(scaled, HUNDRED) >= 0) wide += 1
      else if (cmp(scaled, ONE) >= 0) readable += 1
    }
    return { readable, wide }
  }
  const lakh = score(LAKH)
  const crore = score(CRORE)
  if (crore.readable !== lakh.readable) return crore.readable > lakh.readable ? CRORE : LAKH
  return crore.wide < lakh.wide ? CRORE : LAKH
}

/**
 * The legend's steps: ten equal slices of the range from the largest net-selling figure to the
 * largest net-buying one, five a side. They are also the grid's filter — a tile belongs to the
 * slice its net falls in (`netBin`).
 */
export const LEGEND_STEPS = 10

/**
 * A tile's net prints in 600 from this share of the largest |net| drawn. The mock bolds 9.6 on a
 * range that tops out at 14.8, so a bit under two thirds of the largest.
 */
export const BOLD_FROM = 0.65

/**
 * Which of the legend's ten slices a net lands in, 0 (the heaviest selling) to 9 (the heaviest
 * buying). `max` is the largest |net| drawn, so a day of only level tiles (`max` zero) puts every
 * tile in the first buying slice rather than dividing by it.
 */
export const netBin = (net: Money, max: Money): number => {
  const top = toChartNumber(max)
  if (top === 0) return LEGEND_STEPS / 2
  const slice = Math.floor(((toChartNumber(net) + top) / (2 * top)) * LEGEND_STEPS)
  return Math.max(0, Math.min(LEGEND_STEPS - 1, slice))
}
